import { useMemo } from 'react';
import { useIssueTypeWorkflowMappings } from './useWorkflows';
import type { WorkflowStatusInfo } from '../services/projectService';

interface ProjectWorkflowInfo {
  workflowId: string;
  name: string;
  statuses: WorkflowStatusInfo[];
}

/**
 * 프로젝트 + issueType으로부터 유효 워크플로우 상태를 클라이언트에서 결정한다.
 * issueType별 오버라이드가 있으면 해당 워크플로우, 없으면 프로젝트 기본 워크플로우 반환.
 */
export function useResolvedWorkflowStatuses(
  projectId: string,
  issueType: string | undefined,
  projectWorkflow: ProjectWorkflowInfo | undefined,
) {
  const { data: mappingData } = useIssueTypeWorkflowMappings(projectId);

  return useMemo(() => {
    if (!projectWorkflow) return { statuses: [], workflowId: '', workflowName: '', isOverride: false };

    if (!mappingData || !issueType) {
      return {
        statuses: projectWorkflow.statuses,
        workflowId: projectWorkflow.workflowId,
        workflowName: projectWorkflow.name,
        isOverride: false,
      };
    }

    const override = mappingData.issueTypeWorkflows.find((m) => m.issueType === issueType);
    if (override) {
      return {
        statuses: override.workflow.statuses,
        workflowId: override.workflow.workflowId,
        workflowName: override.workflow.name,
        isOverride: true,
      };
    }

    return {
      statuses: mappingData.defaultWorkflow.statuses,
      workflowId: mappingData.defaultWorkflow.workflowId,
      workflowName: mappingData.defaultWorkflow.name,
      isOverride: false,
    };
  }, [mappingData, issueType, projectWorkflow]);
}

/**
 * 프로젝트에서 활성화된 모든 워크플로우의 상태를 워크플로우별로 그룹핑하여 반환.
 * 필터 패널에서 사용.
 */
export function useAllActiveWorkflowStatuses(
  projectId: string,
  projectWorkflow: ProjectWorkflowInfo | undefined,
) {
  const { data: mappingData } = useIssueTypeWorkflowMappings(projectId);

  return useMemo(() => {
    if (!projectWorkflow) return { groups: [], isMultiWorkflow: false, flatStatuses: [] };

    // 기본 워크플로우
    const workflowMap = new Map<string, { name: string; statuses: WorkflowStatusInfo[] }>();
    const defaultWf = mappingData?.defaultWorkflow ?? projectWorkflow;
    workflowMap.set(defaultWf.workflowId, {
      name: defaultWf.name,
      statuses: defaultWf.statuses,
    });

    // 오버라이드 워크플로우 추가
    if (mappingData) {
      for (const m of mappingData.issueTypeWorkflows) {
        if (!workflowMap.has(m.workflow.workflowId)) {
          workflowMap.set(m.workflow.workflowId, {
            name: m.workflow.name,
            statuses: m.workflow.statuses,
          });
        }
      }
    }

    const groups = Array.from(workflowMap.entries()).map(([workflowId, wf]) => ({
      workflowId,
      workflowName: wf.name,
      statuses: wf.statuses,
    }));

    // 중복 제거된 flat 목록 (name 기반)
    const seenNames = new Set<string>();
    const flatStatuses: WorkflowStatusInfo[] = [];
    for (const group of groups) {
      for (const s of group.statuses) {
        if (!seenNames.has(s.name)) {
          seenNames.add(s.name);
          flatStatuses.push(s);
        }
      }
    }

    return {
      groups,
      isMultiWorkflow: groups.length > 1,
      flatStatuses,
    };
  }, [mappingData, projectWorkflow]);
}
